import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useLifecareStudioData from './useLifecareStudioData';
import useLifecarePlanEstimate from './useLifecarePlanEstimate';
import usePreliminaryPreviewModel from './usePreliminaryPreviewModel';
import usePreliminaryLifecareStatus from './usePreliminaryLifecareStatus';
import {
    getClientDisplayName,
    normalizePlanForForm,
} from '../utils/lifecarePlanUtils';

const resolvePlaintiffName = ({ selectedClient, plan, clients, selectedClientId }) => {
    const directName = selectedClient?.personalInfo?.clientName || plan?.info?.clientName;
    if (directName) return directName;
    const index = clients.findIndex((client) => client?.clientId === selectedClientId);
    return getClientDisplayName({ client: selectedClient, index: index < 0 ? 0 : index });
};

const usePreliminaryLifecarePlan = () => {
    const { caseId } = useParams();
    const navigate = useNavigate();

    const {
        caseName,
        clients,
        isInitialLoadPending,
        lifecareClientIdAndDisplayNames,
        selectedClientId,
        setSelectedClientId,
        selectedClient,
        selectedPlan,
    } = useLifecareStudioData({ caseId, isOpen: true });

    const { getIncreaseForClient } = useLifecarePlanEstimate({ caseId });

    const { hasEstimate, formattedIncrease } = useMemo(
        () => getIncreaseForClient(selectedClientId),
        [getIncreaseForClient, selectedClientId]
    );

    const normalizedPlan = useMemo(
        () => (selectedPlan ? normalizePlanForForm(selectedPlan) : null),
        [selectedPlan]
    );

    const plaintiffName = useMemo(
        () => resolvePlaintiffName({ selectedClient, plan: normalizedPlan, clients, selectedClientId }),
        [selectedClient, normalizedPlan, clients, selectedClientId]
    );

    const dateOfLoss =
        selectedClient?.caseInfo?.dateOfIncident || normalizedPlan?.info?.dateOfIncident || '';

    const previewModel = usePreliminaryPreviewModel({
        plaintiffName,
        dateOfLoss,
        recordsReviewed: undefined,
        plan: normalizedPlan,
        hasEstimate,
        formattedIncrease,
    });

    const preliminaryStatus = usePreliminaryLifecareStatus({ caseId, caseName });

    const handleClose = () => navigate(`/case/${caseId}/demand-studio`);
    const goToCertifiedPlan = () => navigate(`/case/preliminary-lifecare-plan/certified-plan/${caseId}`);

    return {
        caseId,
        caseName,
        isInitialLoadPending,
        lifecareClientIdAndDisplayNames,
        selectedClientId,
        setSelectedClientId,
        previewModel,
        preliminaryStatus,
        onRequestPlan: goToCertifiedPlan,
        handleClose,
    };
};

export default usePreliminaryLifecarePlan;
